import type { TechlineCommentEntity } from '../entities';
import type { BuildCommentBodyParams, CommentBody } from './types';

import { mapStatusCodeToApiString } from './mappers';

/**
 * Construct the API payload for adding a comment to a techline case.
 * Maps the status code to the API string format and assembles all required fields.
 *
 * @param params - the comment details to build the payload from
 * @returns the formatted comment body for the API
 * @example
 * ```ts
 * const body = buildCommentBody({
 *     caseNumber: 'CASE-123',
 *     caseStatusCode: TechlineStatusCode.IN_REVIEW,
 *     username: 'jdoe',
 *     retailerNumber: 'R001',
 *     message: 'Checked the wiring harness.',
 * });
 * ```
 */
export function buildCommentBody(params: BuildCommentBodyParams): CommentBody {
    const { caseNumber, caseStatusCode, message, retailerNumber, username } = params;

    return {
        casenumber: caseNumber,
        message,
        retailernumber: retailerNumber,
        techlinecasestatus: mapStatusCodeToApiString(caseStatusCode),
        username,
    };
}

/**
 * Sort techline comment entities by their creation date in descending order (newest first).
 * Returns a new array; does not mutate the input.
 *
 * @param comments - the comments to sort
 * @returns a new array sorted by `createdOn` descending
 * @example
 * ```ts
 * const sorted = sortCommentsByDate([
 *     { createdOn: '2024-01-01', fullName: 'A', jobTitle: '', message: '', retailerNumber: '', userName: '' },
 *     { createdOn: '2024-06-15', fullName: 'B', jobTitle: '', message: '', retailerNumber: '', userName: '' },
 * ]);
 * // sorted[0].createdOn === '2024-06-15'
 * ```
 */
export function sortCommentsByDate(comments: TechlineCommentEntity[]): TechlineCommentEntity[] {
    return [...comments].sort((a, b) => {
        return new Date(b.createdOn).getTime() - new Date(a.createdOn).getTime();
    });
}
